import { all, call, put, select, takeEvery, takeLatest } from 'typed-redux-saga';

import { logError } from 'src/loggingManager';
import { RELOAD } from 'src/store/actions/actionTypes';
import { RootState } from 'src/store';
import { getSurfaceProperties } from 'src/selectors/pages/editor';
import { retrieveDesignData, RetrieveDesignDataParams } from 'src/services/disClient';
import { fetchProductModel } from 'src/services/productModelClient';
import { resolveEffectiveSku } from 'src/util/skuKey';
import type { DesignDataResultType } from 'src/types/decorationSpecs';
import type { GenericSelectorProductType } from 'src/types/product';
import { applyVariables, changeEditorSkuSuccess } from 'src/pages/editor/components/surfaces/slice';
import {
    retrieveDecorationSpecs,
    retrieveDecorationSpecsSettled,
    requestProductModel,
    productModelResolved,
} from './slice';

const getCacheKey = (state: RootState) => state.pages.editor.decorationSpecs.cacheKey;
const getProductModelKey = (state: RootState) => state.pages.editor.decorationSpecs.productModelKey;

function buildCacheKey({
    cimId,
    cimVersion,
    variables,
}: {
    cimId: string;
    cimVersion?: number;
    variables?: Record<string, string | undefined>;
}): string {
    return `${cimId}-${cimVersion}-${JSON.stringify(variables ?? {})}`;
}

/** Resolves the DIS fetch's result, converting a real (non-404/400) error into a message rather
 * than letting an uncaught rejection abort `fetchDecorationSpecs` entirely. */
function* fetchDesignDataSafely(
    params: RetrieveDesignDataParams,
): Generator<any, { data?: DesignDataResultType; error?: string }, any> {
    try {
        const data = yield* call(retrieveDesignData, params);
        return { data };
    } catch (error: any) {
        logError(`Failed to fetch DIS design data for decoration specs panel: ${error}`);
        return { error: (error && error.message) || 'Failed to fetch decoration specs' };
    }
}

/**
 * Fetches the full product model definition (options and property rules/assignments) from api.products.cimpress.io
 * if it hasn't been fetched for the current effective SKU and version yet.
 */
export function* fetchProductModelIfNeeded(): Generator<any, void, any> {
    const { sku, skuVersion, merchantSku, merchantVersion } = yield* select(getSurfaceProperties);
    const { effectiveSku, effectiveVersion } = resolveEffectiveSku({
        sku,
        skuVersion,
        merchantSku,
        merchantVersion,
    });

    if (!effectiveSku) {
        return;
    }

    const modelKey = effectiveVersion !== undefined ? `${effectiveSku}-${effectiveVersion}` : effectiveSku;
    const currentModelKey = yield* select(getProductModelKey);
    if (currentModelKey === modelKey) {
        // Already fetched (or already in flight, since `requestProductModel` below sets this
        // optimistically before the request resolves) for this exact product/version.
        return;
    }

    // Set optimistically before the request resolves, so a slower/superseded response can be
    // told apart from the current one in `productModelResolved` (see that reducer's comment).
    yield* put(requestProductModel({ productModelKey: modelKey }));

    try {
        const productModel: GenericSelectorProductType | null = yield* call(fetchProductModel, {
            productId: effectiveSku,
            version: effectiveVersion,
        });
        yield* put(productModelResolved({ productModelKey: modelKey, productModel }));
    } catch (error: any) {
        logError(`Failed to fetch product model for ${modelKey}: ${error}`);
        yield* put(productModelResolved({ productModelKey: modelKey, productModel: null }));
    }
}

/**
 * Fetches decoration-placement/print-white data from DIS for the current product/selection.
 * DIS Gearbox rules are keyed strictly on CIM ID, CIM version, CIM properties, and CIM surfaces.
 */
export function* fetchDecorationSpecs(): Generator<any, void, any> {
    const { sku, skuVersion, merchantVariables } = yield* select(getSurfaceProperties);

    // CIM ID is stored in `sku`.
    const cimId = sku;
    const cimVersion = skuVersion;

    // Nothing to look up yet (no CIM product chosen/translated yet).
    if (!cimId) {
        return;
    }

    // Deliberately `merchantVariables`, not `variables` — `applyVariables` (dispatched directly
    // by the variable picker) updates `merchantVariables` synchronously and wholesale on every
    // pick/removal, so it always reflects exactly what's currently selected, including a
    // just-removed property. `variables` is instead refreshed by a separate saga
    // (`surfaces/saga.ts`'s `getSurfaces`) only once the picker's own selection is fully resolved,
    // and only after an async round trip — so it can sit stale (reflecting a previous, now-wrong
    // complete selection) for an arbitrary stretch after a property changes, which showed up live
    // as decoration-placement data not clearing on removal and surfacing stale values after a new
    // complete pick. `merchantVariables` has no such lag.
    const cacheKey = buildCacheKey({ cimId, cimVersion, variables: merchantVariables });
    const currentCacheKey = yield* select(getCacheKey);
    if (currentCacheKey === cacheKey) {
        // Product identity and every variable are unchanged since the last fetch for this
        // selection — nothing new to look up. Refetching on every unrelated action (e.g. a surface
        // page switch) would be a wasted DIS call.
        return;
    }

    yield* put(retrieveDecorationSpecs({ cacheKey }));

    // Call DIS directly and trust its own signaling for an incomplete selection (400 with
    // `missingSelections`) or no usable data (404) — see `disClient.ts`. A prior proactive
    // pre-check against this CIM's full declared option list was removed: that list includes
    // every option/property the CIM declares (including static, non-merchandising attributes
    // like Brand/Material, and sub-options that only apply when a sibling toggle like
    // "Deco 2 Enabled" is on), which is a much larger set than what the picker ever resolves into
    // `merchantVariables` — so the pre-check could never be satisfied and permanently blocked the
    // DIS call for CIM-only products. DIS resolving rules directly from CIM properties is the
    // authoritative source now, so there's no separate narrower axis list for Scene Maker to
    // check completeness against.
    const designResult = (yield* call(fetchDesignDataSafely, {
        cimId,
        cimVersion,
        variables: merchantVariables,
    })) as {
        data?: DesignDataResultType;
        error?: string;
    };

    yield* put(
        retrieveDecorationSpecsSettled({
            cacheKey,
            decorationPlacement: designResult.data?.decorationPlacement ?? null,
            printWhite: designResult.data?.printWhite ?? null,
            decorationMissingSelections: designResult.data?.missingSelections ?? null,
            error: designResult.error,
        }),
    );
}

export default function* decorationSpecsSaga() {
    return yield* all([
        // takeLatest, not takeEvery: without a completeness pre-check (removed above), every
        // single option pick now reaches DIS directly. A rapid sequence of picks previously
        // relied on the pre-check to skip most of the resulting network calls; takeLatest
        // bounds call volume to roughly one in-flight DIS request at a time by cancelling an
        // older, now-superseded fetch as soon as a newer applyVariables fires, rather than
        // leaving it to complete and be discarded by retrieveDecorationSpecsSettled's cacheKey
        // guard.
        yield* takeLatest(applyVariables, fetchDecorationSpecs),
        yield* takeEvery(changeEditorSkuSuccess, function* () {
            yield* call(fetchProductModelIfNeeded);
            yield* call(fetchDecorationSpecs);
        }),
        yield* takeEvery(RELOAD, function* () {
            yield* call(fetchProductModelIfNeeded);
            yield* call(fetchDecorationSpecs);
        }),
    ]);
}
